/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { Fragment, ReactElement } from 'react';
import styled, { CSSObject } from '@emotion/styled';
import { getPatternModal } from '../../../style';

import Avatar from '../../../atoms/Avatar';
import Headline from '../../common/Headline';
import Button from '../../../atoms/buttons/Button';
import ButtonSet from '../../blocks/ButtonSet';
import { connect } from 'react-redux';
import { ProfileState } from '../../../reducers/profile';
import { ProfileMaker } from '../../../models/Profile';
import useMediaBreakpoints from '../../../hooks/useMediaBreakpoints';
import { RootState } from '../../../store';
import OfferModalFooter from './OfferModalFooter';
import ModalWithPattern from 'components/common/modals/ModalWithPattern';
import { IsMobileViewPort } from 'utils/helpers';
import { mobileOverlayStyle, mobileContentStyle } from 'components/common/CommonStyle';
import breakpoints from 'theme/material/breakpoints';
import borders from 'theme/borders';

const Form = styled('form')({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
});

const HeaderDiv = styled('div')({
  textAlign: 'center',

  [breakpoints.down(639.05)]: {
    marginTop: 10,
  },
});

const AvatarTitleDiv = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  flexWrap: 'wrap',
  justifyContent: 'center',
  alignItems: 'center',
  margin: '0 auto',
  gap: 16,
  [breakpoints.down(639.05)]: {
    gap: 10,
  },
});

const TitleDiv = styled('span')(
  (): CSSObject => ({
    color: getPatternModal().titleColor,
    fontSize: 36,
    fontWeight: 800,
    lineHeight: '40px',
    letterSpacing: -0.3,
    textTransform: 'uppercase',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    [breakpoints.down(639.05)]: {
      fontSize: 30,
    },
  })
);

const Content = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  justifyContent: 'space-between',
});

const ButtonDiv = styled('div')(() => ({
  margin: '20px 0',
}));

const getHeadline = (isCounter = false, username, isRevised = false, isGenerous, subTitleOne = '') => {
  if (subTitleOne) {
    return <span>{subTitleOne}</span>;
  } else {
    return isRevised ? (
      <span>
        Your revised offer {isGenerous ? '' : 'request'} to <strong>{username}</strong> has been sent!
      </span>
    ) : (
      <span>
        Your {isCounter ? 'counter offer' : 'offer'} to <strong>{username}</strong> has been sent!
      </span>
    );
  }
};

interface OfferSuccessProps {
  isModalOpen: boolean;
  onClose: () => void;
  offerAmount: any;
  avatarSrc: any;
  username: any;
  buttonID: any;
  isCounter: any;
  isRevised?: boolean;
  isSuggest?: boolean;
  profile: ProfileState;
  mainTitle?: string;
  subTitleOne?: string;
  subTitleTwo?: string;
  customHeaderStyle?: React.CSSProperties;
  customTitleStyle?: React.CSSProperties;
  customContentStyle?: any;
}

const OfferSuccess = ({
  isModalOpen,
  onClose,
  offerAmount,
  avatarSrc,
  username,
  buttonID,
  isCounter,
  isRevised = false,
  isSuggest = false,
  profile,
  mainTitle = '',
  subTitleOne = '',
  subTitleTwo = '',
  customHeaderStyle = {},
  customTitleStyle = {},
  customContentStyle = {},
}: OfferSuccessProps): ReactElement => {
  const { isMobile } = useMediaBreakpoints();
  const profileModel = ProfileMaker.create(profile.profile);
  const isGenerous = profileModel.isGenerous();

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (sessionStorage.getItem('hasAddedPhoto')) {
      sessionStorage.removeItem('hasAddedPhoto');
    }
    onClose();
  };

  const renderTitleLabel = () => {
    if (mainTitle) {
      return mainTitle;
    } else {
      if ((isRevised || isSuggest) && offerAmount === '') {
        return 'Suggest offer sent!';
      } else {
        return `${offerAmount} ${isGenerous ? 'Offer' : 'Request'} Sent!`;
      }
    }
  };

  return (
    <Fragment>
      <ModalWithPattern
        isModalOpen={isModalOpen}
        dataTestID="offer-success"
        closeBtnClassName="offer-success-modal-close"
        closeBtnDataTestId="offer-success-modal-close"
        modalContentLabel="Success Offer Modal"
        onCloseClick={onClose}
        closeRequest={onClose}
        customOverlayStyle={IsMobileViewPort ? mobileOverlayStyle : {}}
        customContentStyle={IsMobileViewPort ? { ...mobileContentStyle, ...customContentStyle } : customContentStyle}
        withoutBgPattern={false}
        hideCloseBtn={false}
        closeOnOverlayClick={true}
        enableTouchMove={true}
      >
        <Form>
          <HeaderDiv style={customHeaderStyle}>
            <AvatarTitleDiv>
              {avatarSrc && (
                <Avatar
                  data-test-id="offer-modal-avatar"
                  src={avatarSrc}
                  width={'100px'}
                  customStyle={{
                    backgroundColor: '#ccc',
                    border: `1px solid ${borders.light.primary}`,
                  }}
                />
              )}
              <TitleDiv style={customTitleStyle}>{renderTitleLabel()}</TitleDiv>
            </AvatarTitleDiv>
            <Headline
              style={{
                color: getPatternModal().headerColor,
                marginTop: 5,
                fontSize: 16,
                lineHeight: '21px',
                letterSpacing: '-0.3px',
              }}
              data-test-id="profile-offer-headline"
              size="SM"
            >
              {getHeadline(isCounter, username, isRevised, isGenerous, subTitleOne)}
            </Headline>
            {subTitleTwo && (
              <Headline
                style={{
                  color: getPatternModal().headerColor,
                  fontSize: '16px',
                  fontWeight: 700,
                  lineHeight: '21px',
                  letterSpacing: '-0.32px',
                }}
              >
                {subTitleTwo}
              </Headline>
            )}
          </HeaderDiv>
          <Content>
            <ButtonDiv>
              <ButtonSet align="center">
                <Button
                  id={buttonID}
                  buttonType="primaryRed"
                  onClick={handleSubmit}
                  type="submit"
                  customStyle={{
                    fontSize: 18,
                    lineHeight: '16px',
                    padding: '16px 32px',
                    borderWidth: 0,
                    [breakpoints.down(639.05)]: {
                      fontSize: 14,
                      padding: '12px 22px',
                    },
                  }}
                  customBtnContentStyle={{
                    fontWeight: isMobile ? 600 : 700,
                    letterSpacing: isMobile ? -0.28 : -0.36,
                  }}
                >
                  Close
                </Button>
              </ButtonSet>
            </ButtonDiv>
          </Content>
        </Form>
        <OfferModalFooter />
      </ModalWithPattern>
    </Fragment>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    profile: state.profile,
  };
};

export default connect(mapStateToProps, {})(OfferSuccess);
